<script setup lang="ts">
import { ref, watch, nextTick } from "vue";

const props = defineProps<{
  modelValue: boolean;
  title: string;
  value?: string;
  placeholder?: string;
  loading?: boolean;
  confirmLabel?: string;
  cancelLabel?: string;
}>();

const emit = defineEmits<{
  (e: "update:modelValue", value: boolean): void;
  (e: "confirm", value: string): void;
}>();

const inputValue = ref(props.value || "");
const inputRef = ref<HTMLInputElement | null>(null);

watch(
  () => props.modelValue,
  (show) => {
    if (show) {
      inputValue.value = props.value || "";
      nextTick(() => {
        inputRef.value?.focus();
        inputRef.value?.select();
      });
    }
  }
);

watch(
  () => props.value,
  (newValue) => {
    if (props.modelValue) {
      inputValue.value = newValue || "";
    }
  }
);

const close = () => {
  emit("update:modelValue", false);
};

const confirm = () => {
  emit("confirm", inputValue.value.trim());
};
</script>

<template>
  <Teleport to="body">
    <div v-if="modelValue" class="modal-overlay" @click.self="close">
      <div class="modal-content">
        <h4>{{ title }}</h4>
        <input
          ref="inputRef"
          v-model="inputValue"
          type="text"
          class="modal-input"
          :placeholder="placeholder"
          @keyup.enter="confirm"
        />
        <div class="modal-actions">
          <button class="modal-btn cancel" @click="close">
            {{ cancelLabel || $t("cancel") }}
          </button>
          <button
            class="modal-btn confirm"
            :disabled="loading"
            @click="confirm"
          >
            <span
              v-if="loading"
              class="spinner-border spinner-border-sm me-2"
            />
            {{ confirmLabel || $t("save") }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped lang="scss">
.modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 2000;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.modal-content {
  width: 100%;
  max-width: 400px;
  background-color: white;
  border-radius: 16px;
  padding: 24px;

  h4 {
    margin: 0 0 16px;
    font-size: 18px;
    font-weight: 600;
    color: var(--bs-body-color);
  }
}

.modal-input {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid var(--bs-border-color);
  border-radius: 12px;
  background-color: var(--bs-body-bg);
  font-size: 16px;
  color: var(--bs-body-color);

  &:focus {
    outline: none;
    border-color: var(--bs-primary);
  }
}

.modal-actions {
  display: flex;
  gap: 12px;
  margin-top: 20px;
}

.modal-btn {
  flex: 1;
  padding: 12px 20px;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;

  &.cancel {
    background-color: var(--bs-secondary-bg);
    color: var(--bs-body-color);

    &:hover {
      background-color: var(--bs-tertiary-bg);
    }
  }

  &.confirm {
    background-color: var(--bs-primary);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      opacity: 0.9;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
}
</style>
